import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import PlusIcon from "@icons/myicons/plus-circled.svg?react";
import { LoadingPlaceHolder, TypographyWithInfoIcon } from "@components";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import {
  useGetAccountsByProvider,
  useGetBankConnectionByProvider,
} from "@features/settings/useSettings";
import { AccountRow } from "@features/settings/SettingsBankConnections/AccountRow";
import { InterfaceRow } from "@features/settings/SettingsBankConnections/InterfaceRow";
import { SettingsBankConnectionMenu } from "@features/settings/SettingsBankConnections/SettingsBankConnectionMenu";
import { useAccounts } from "@api/v1/hooks/useAccounts";
import { useMyself } from "@api";
import { useBankImportReturnUrl, useRoutes } from "@hooks";

const AccountsContainer = styled(Box)(({ theme }) => ({
  "& > div:not(:last-child)": {
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
  },
  marginBottom: theme.spacing(7),
}));

export const SettingsBankConnectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: myself } = useMyself();
  const { id, apiProvider } = useParams<{
    id: string;
    apiProvider: "FIN_API" | "WEALTH_API";
  }>();
  const [submittingOp, setSubmittingOp] = useState<
    "accounts" | "interfaces" | null
  >(null);
  const { saveReturnUrl } = useBankImportReturnUrl();
  const { pushAddAccountsToConnection, pushAddInterfacesToConnection } =
    useRoutes();
  const { data: accounts } = useAccounts({
    bankConnectionId: [Number(id)],
  });
  const {
    data: bankConnection,
    isFetching,
    error,
  } = useGetBankConnectionByProvider(apiProvider || "FIN_API", {
    id: Number(id),
  });

  // the connection no longer exists (deleted, or a stale bookmark/back
  // navigation) — leave rather than render an empty settings shell
  useEffect(() => {
    if (error?.response?.status === 404) {
      navigate("/settings/advanced", { replace: true });
    }
  }, [error, navigate]);

  const { data: remoteAccounts, isFetching: isAccountsLoading } =
    useGetAccountsByProvider(apiProvider || "FIN_API", {
      bankConnectionId: [Number(id)],
    });

  const handleAddAccounts = async () => {
    if (!bankConnection?.id || submittingOp) return;
    setSubmittingOp("accounts");
    try {
      saveReturnUrl();
      await pushAddAccountsToConnection(
        bankConnection.id,
        apiProvider || "FIN_API"
      );
    } finally {
      setSubmittingOp(null);
    }
  };

  const handleAddInterfaces = async () => {
    if (!bankConnection?.id || submittingOp) return;
    setSubmittingOp("interfaces");
    try {
      saveReturnUrl();
      await pushAddInterfacesToConnection(
        bankConnection.id,
        apiProvider || "FIN_API"
      );
    } finally {
      setSubmittingOp(null);
    }
  };

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.bankConnections.title") },
        { text: bankConnection?.name || "", isFetching },
      ]}
      title={bankConnection?.name || ""}
      subtitle={t("settings.bankConnection.subtitle")}
      actionButton={
        <SettingsBankConnectionMenu
          bankConnection={bankConnection}
          provider={apiProvider || "FIN_API"}
        />
      }
    >
      {bankConnection ? (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2">
              {t("settings.bankConnection.accounts.title")}
            </Typography>
            <Box display="flex" gap={1}>
              {myself?.roles?.includes("ROLE_QUALITY_ASSURANCE") && (
                <IconButton
                  size="small"
                  onClick={handleAddInterfaces}
                  disabled={!!submittingOp}
                >
                  {submittingOp === "interfaces" ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <PlusIcon />
                  )}
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={handleAddAccounts}
                disabled={!!submittingOp}
              >
                {submittingOp === "accounts" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PlusIcon />
                )}
              </IconButton>
            </Box>
          </Box>
          <AccountsContainer>
            {isAccountsLoading ? (
              <LoadingPlaceHolder />
            ) : (
              remoteAccounts?.map((remoteAccount) => {
                return (
                  <AccountRow
                    account={remoteAccount}
                    key={remoteAccount.id}
                    provider={apiProvider}
                    accounts={accounts?.accounts}
                  />
                );
              })
            )}
          </AccountsContainer>
          <Divider />
          <Box mt={12.5}>
            <Box mb={5.5}>
              <TypographyWithInfoIcon
                variant="subtitle2"
                tooltipText={
                  <Box>{t("settings.bankConnection.interfaces.info")}</Box>
                }
              >
                {t("settings.bankConnection.interfaces.title")}
              </TypographyWithInfoIcon>
            </Box>
            <Box display="flex" flexDirection="column" gap={4}>
              {bankConnection?.interfaces?.map((interfaceItem) => (
                <InterfaceRow connectionInterface={interfaceItem} />
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}
    </SettingsLayout>
  );
};
